import { apiFetch } from './apiClient.js';

/**
 * #164 — Cliente UI para upload de media WhatsApp (3-step):
 *   1. issueUploadUrl → presigned PUT
 *   2. PUT directo del browser al S3
 *   3. finalize → backend lo manda a Meta y devuelve mediaId
 */

export interface IssueUploadUrlResp {
  url: string;
  headers?: Record<string, string>;
  expiresInSeconds: number;
  objectKey: string;
}

export interface FinalizeResp {
  mediaId: string;
  contentType: string;
  ttlSeconds: number;
}

export async function issueWaMediaUploadUrl(
  tenantId: string, connectorId: string,
  body: { fileName: string; contentType: string },
): Promise<IssueUploadUrlResp> {
  return apiFetch<IssueUploadUrlResp>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/connectors/${encodeURIComponent(connectorId)}/media/upload-url`,
    { method: 'POST', body },
  );
}

export async function finalizeWaMediaUpload(
  tenantId: string, connectorId: string,
  body: { objectKey: string; contentType: string },
): Promise<FinalizeResp> {
  return apiFetch<FinalizeResp>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/connectors/${encodeURIComponent(connectorId)}/media/finalize`,
    { method: 'POST', body },
  );
}

/** Hace el PUT al S3 con los headers del presigned. */
export async function putToS3(
  signed: IssueUploadUrlResp,
  file: File,
): Promise<void> {
  const res = await fetch(signed.url, {
    method: 'PUT',
    headers: { 'content-type': file.type, ...(signed.headers ?? {}) },
    body: file,
  });
  if (!res.ok) {
    throw new Error(`PUT a S3 falló (${res.status}): ${await res.text().catch(() => '')}`);
  }
}

/** End-to-end helper: presigned → PUT → finalize. */
export async function uploadWaMediaEndToEnd(
  tenantId: string, connectorId: string, file: File,
): Promise<FinalizeResp> {
  const signed = await issueWaMediaUploadUrl(tenantId, connectorId, {
    fileName: file.name, contentType: file.type || 'application/octet-stream',
  });
  await putToS3(signed, file);
  return finalizeWaMediaUpload(tenantId, connectorId, {
    objectKey: signed.objectKey,
    contentType: file.type || 'application/octet-stream',
  });
}

/**
 * #179 — Sube el archivo de MUESTRA del header de un template y devuelve el
 * handle de Meta.
 *
 * No confundir con `uploadWaMediaEndToEnd`: aquello devuelve un `media_id`,
 * que sirve para ENVIAR un mensaje. Para CREAR un template con header
 * multimedia, Meta exige un handle de la Resumable Upload API y rechaza el
 * media_id. Comparten la subida a S3, no el paso final.
 */
export async function uploadTemplateHeaderSample(
  tenantId: string, connectorId: string, file: File,
): Promise<{ handle: string }> {
  const contentType = file.type || 'application/octet-stream';
  const signed = await issueWaMediaUploadUrl(tenantId, connectorId, {
    fileName: file.name, contentType,
  });
  await putToS3(signed, file);
  return apiFetch<{ handle: string }>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/connectors/${encodeURIComponent(connectorId)}/media/template-sample`,
    { method: 'POST', body: { objectKey: signed.objectKey, fileName: file.name, mimeType: contentType } },
  );
}
